/* geo — the little bit of geometry Crew Mode's GPS clock-in needs.
   Site coordinates come from the job's own record when the office pinned
   them, otherwise from the estimate's parcel (the lot the fence was drawn
   on). No network, no map library: haversine on two points. */
export type LatLng = [number, number];

const R_MI = 3958.7613;
export function distanceMiles(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const la1 = toRad(a[0]), la2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R_MI * Math.asin(Math.min(1, Math.sqrt(h)));
}

function validLL(p: any): p is LatLng {
  return Array.isArray(p) && p.length >= 2 && isFinite(+p[0]) && isFinite(+p[1]) && Math.abs(+p[0]) <= 90 && Math.abs(+p[1]) <= 180 && !(+p[0] === 0 && +p[1] === 0);
}
function centroidOf(poly: any): LatLng | null {
  if (!Array.isArray(poly) || poly.length < 3) return null;
  let la = 0, ln = 0, n = 0;
  for (const p of poly) { if (validLL(p)) { la += +p[0]; ln += +p[1]; n++; } }
  return n ? [la / n, ln / n] : null;
}

/* where the job is: wo.geo ([lat,lng] the office set) → the quote's parcel
   centre → the parcel polygon's centroid → the query point the estimator
   searched. null when the job has no drawn lot (manual quote, no address). */
export function siteCoords(wo: any, store: any): LatLng | null {
  if (!wo) return null;
  if (validLL(wo.geo)) return [+wo.geo[0], +wo.geo[1]];
  // the office's geocode is { lat, lng } (workorders.tsx WeatherChip, the schedule board)
  if (wo.geo && typeof wo.geo === "object" && validLL([wo.geo.lat, wo.geo.lng])) return [+wo.geo.lat, +wo.geo.lng];
  const q = (store && store.quotes || []).find((x: any) => x && x.id === wo.quoteId);
  // a concrete quote's drawn site (lib/slabs.ts inputs.concrete.site.center), either shape
  const cs = q && q.inputs && q.inputs.concrete && q.inputs.concrete.site && q.inputs.concrete.site.center;
  if (validLL(cs)) return [+cs[0], +cs[1]];
  if (cs && typeof cs === "object" && validLL([cs.lat, cs.lng])) return [+cs.lat, +cs.lng];
  const par = q && q.inputs && q.inputs.parcel;
  if (par) {
    if (validLL(par.center)) return [+par.center[0], +par.center[1]];
    const c = centroidOf(par.polygon) || centroidOf(par.rawPolygon);
    if (c) return c;
    if (validLL(par.queryPoint)) return [+par.queryPoint[0], +par.queryPoint[1]];
  }
  const mv = q && q.inputs && q.inputs.mVerts;
  const c2 = centroidOf(mv);
  return c2;
}

/* one GPS fix, with a timeout so a phone with location off doesn't hang the
   button. Rejects with a readable message. */
export function currentPosition(opts: { timeoutMs?: number } = {}): Promise<{ lat: number; lng: number; accuracyM: number }> {
  return new Promise((resolve, reject) => {
    const g = typeof navigator !== "undefined" ? navigator.geolocation : null;
    if (!g) { reject(new Error("This device can't report its location.")); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; reject(new Error("Location timed out — step outside or check location permission.")); } }, opts.timeoutMs || 15000);
    g.getCurrentPosition(
      (p) => { if (done) return; done = true; clearTimeout(t); resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: p.coords.accuracy || 0 }); },
      (err) => { if (done) return; done = true; clearTimeout(t);
        const msg = err && err.code === 1 ? "Location permission is off for this app — allow it in the phone's settings to clock in." : err && err.code === 2 ? "Couldn't get a GPS fix — try again outside." : "Location timed out — try again.";
        reject(new Error(msg)); },
      { enableHighAccuracy: true, timeout: opts.timeoutMs || 15000, maximumAge: 30000 }
    );
  });
}

/* the clock-in policy from settings: radius in miles (0.1 default), off when
   the office turned it off. `overrideCode` is optional — when set, an
   on-site override needs it; when blank the override only needs a reason. */
export function clockInPolicy(settings: any) {
  const s = settings || {};
  const on = s.clockInGps == null ? true : !!s.clockInGps;
  const r = Number(s.clockInRadiusMi);
  return { on, radiusMi: isFinite(r) && r > 0 ? r : 0.1, overrideCode: String(s.clockInOverrideCode || "").trim() };
}
